import React, { useState } from 'react';
import { BowelMovement } from './types';
import { ErrorMessage } from './components/common/ErrorMessage';
import { validationMessages } from './utils/errorMessages';

interface BowelMovementFormProps {
  onSubmit: (data: BowelMovement) => void;
  initialData?: BowelMovement;
  onCancel?: () => void;
}

export const BowelMovementForm: React.FC<BowelMovementFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const now = new Date();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    date: initialData?.date || now.toISOString().split('T')[0],
    time: initialData?.time || now.toTimeString().slice(0, 5),
    consistency: (initialData?.consistency || 'normalna') as 'płynna' | 'miękka' | 'normalna' | 'twarda',
    hasBlood: initialData?.hasBlood || false,
    bloodAmount: initialData?.bloodAmount ?? 1,
    bloodType: (initialData?.bloodType || 'jasna') as 'jasna' | 'żywa' | 'ciemna' | 'wymieszana',
    hasMucus: initialData?.hasMucus || false,
    mucusAmount: initialData?.mucusAmount ?? 1,
    pain: initialData?.pain ?? 0,
    amount: (initialData?.amount || 'średnia') as 'mała' | 'średnia' | 'duża',
    hasGas: initialData?.hasGas || false,
    gasIntensity: initialData?.gasIntensity ?? 1,
    urgency: initialData?.urgency ?? 1,
    otherSymptoms: initialData?.otherSymptoms || ''
  });

  const validateForm = (data: { date: string; time: string; consistency: string }) => {
    const newErrors: Record<string, string> = {};
    if (!data.date) newErrors.date = validationMessages.required;
    if (!data.time) newErrors.time = validationMessages.required;
    if (!data.consistency) newErrors.consistency = 'Wybierz konsystencję';
    if (data.date && new Date(data.date) > new Date()) {
      newErrors.date = validationMessages.futureDate;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!validateForm(formData)) return;

    const dataToSave: BowelMovement = {
      id: initialData?.id || Date.now().toString(),
      date: formData.date,
      time: formData.time,
      consistency: formData.consistency,
      hasBlood: formData.hasBlood,
      bloodAmount: formData.hasBlood && formData.bloodAmount >= 1 ? formData.bloodAmount : undefined,
      bloodType: formData.hasBlood ? formData.bloodType : undefined,
      hasMucus: formData.hasMucus,
      mucusAmount: formData.hasMucus && formData.mucusAmount >= 1 ? formData.mucusAmount : undefined,
      pain: formData.pain,
      amount: formData.amount,
      hasGas: formData.hasGas,
      gasIntensity: formData.hasGas && formData.gasIntensity >= 1 ? formData.gasIntensity : undefined,
      urgency: formData.urgency >= 1 ? formData.urgency : 1,
      otherSymptoms: formData.otherSymptoms || ''
    };
    onSubmit(dataToSave);
    
    if (!initialData) {
      const now = new Date();
      setFormData({
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().slice(0, 5),
        consistency: 'normalna' as 'płynna' | 'miękka' | 'normalna' | 'twarda',
        hasBlood: false,
        bloodAmount: 1,
        bloodType: 'jasna' as 'jasna' | 'żywa' | 'ciemna' | 'wymieszana',
        hasMucus: false,
        mucusAmount: 1,
        pain: 0,
        amount: 'średnia' as 'mała' | 'średnia' | 'duża',
        hasGas: false,
        gasIntensity: 1,
        urgency: 1,
        otherSymptoms: ''
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Data i czas */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => { setFormData({ ...formData, date: e.target.value }); if (errors.date) setErrors({ ...errors, date: '' }); }}
            className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue ${errors.date ? 'border-2 border-red-500' : 'border border-gray-200'}`}
          />
          {errors.date && <ErrorMessage message={errors.date} type="inline" />}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Godzina *</label>
          <input
            type="time"
            value={formData.time}
            onChange={(e) => { setFormData({ ...formData, time: e.target.value }); if (errors.time) setErrors({ ...errors, time: '' }); }}
            className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue ${errors.time ? 'border-2 border-red-500' : 'border border-gray-200'}`}
          />
          {errors.time && <ErrorMessage message={errors.time} type="inline" />}
        </div>
      </div>

      {/* Konsystencja i ilość */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Konsystencja *</label>
          <select
            value={formData.consistency}
            onChange={(e) => { setFormData({ ...formData, consistency: e.target.value as 'płynna' | 'miękka' | 'normalna' | 'twarda' }); if (errors.consistency) setErrors({ ...errors, consistency: '' }); }}
            className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue ${errors.consistency ? 'border-2 border-red-500' : 'border border-gray-200'}`}
          >
            <option value="płynna">Płynna</option>
            <option value="miękka">Miękka</option>
            <option value="normalna">Normalna</option>
            <option value="twarda">Twarda</option>
          </select>
          {errors.consistency && <ErrorMessage message={errors.consistency} type="inline" />}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ilość</label>
          <select
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value as 'mała' | 'średnia' | 'duża' })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue"
          >
            <option value="mała">Mała</option>
            <option value="średnia">Średnia</option>
            <option value="duża">Duża</option>
          </select>
        </div>
      </div>

      {/* Krew */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={formData.hasBlood}
            onChange={(e) => setFormData({ ...formData, hasBlood: e.target.checked })}
            className="w-4 h-4 text-primary-purple border-gray-300 rounded focus:ring-primary-blue"
          />
          Krew
        </label>
      </div>

      {formData.hasBlood && (
        <div className="ml-6 space-y-3 p-3 bg-[#FFEDED] rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ilość krwi: {formData.bloodAmount}/5
            </label>
            <input
              type="range"
              min="0"
              max="5"
              value={formData.bloodAmount}
              onChange={(e) => setFormData({ ...formData, bloodAmount: parseInt(e.target.value) })}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rodzaj krwi</label>
            <select
              value={formData.bloodType}
              onChange={(e) => setFormData({ ...formData, bloodType: e.target.value as 'jasna' | 'żywa' | 'ciemna' | 'wymieszana' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue"
            >
              <option value="jasna">Jasna</option>
              <option value="żywa">Żywa</option>
              <option value="ciemna">Ciemna</option>
              <option value="wymieszana">Wymieszana</option>
            </select>
          </div>
        </div>
      )}

      {/* Śluz */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={formData.hasMucus}
            onChange={(e) => setFormData({ ...formData, hasMucus: e.target.checked })}
            className="w-4 h-4 text-primary-purple border-gray-300 rounded focus:ring-primary-blue"
          />
          Śluz
        </label>
      </div>

      {formData.hasMucus && (
        <div className="ml-6 p-3 bg-[#E8F5ED] rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ilość śluzu: {formData.mucusAmount}/5
          </label>
          <input
            type="range"
            min="0"
            max="5"
            value={formData.mucusAmount}
            onChange={(e) => setFormData({ ...formData, mucusAmount: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>
      )}

      {/* Ból */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Ból/dyskomfort: {formData.pain}/5
        </label>
        <input
          type="range"
          min="0"
          max="5"
          value={formData.pain}
          onChange={(e) => setFormData({ ...formData, pain: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      {/* Gazy */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={formData.hasGas}
            onChange={(e) => setFormData({ ...formData, hasGas: e.target.checked })}
            className="w-4 h-4 text-primary-purple border-gray-300 rounded focus:ring-primary-blue"
          />
          Gazy
        </label>
      </div>

      {formData.hasGas && (
        <div className="ml-6 p-3 bg-[#FFF4ED] rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Intensywność gazów: {formData.gasIntensity}/5
          </label>
          <input
            type="range"
            min="0"
            max="5"
            value={formData.gasIntensity}
            onChange={(e) => setFormData({ ...formData, gasIntensity: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>
      )}

      {/* Pilność */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pilność: {formData.urgency}/5
        </label>
        <input
          type="range"
          min="0"
          max="5"
          value={formData.urgency}
          onChange={(e) => setFormData({ ...formData, urgency: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      {/* Inne objawy */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Inne objawy</label>
        <textarea
          value={formData.otherSymptoms}
          onChange={(e) => setFormData({ ...formData, otherSymptoms: e.target.value })}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-primary-blue"
          placeholder="np. biegunka, zaparcie, wzdęcia..."
        />
      </div>

      {/* Przyciski */}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-gradient-to-r from-[#6B7FFF] to-[#8B5CF6] text-white rounded-2xl text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {initialData ? 'Zapisz zmiany' : 'Zapisz wypróżnienie'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-2xl text-sm font-medium hover:bg-gray-300 transition-colors"
          >
            Anuluj
          </button>
        )}
      </div>
    </form>
  );
};
